import React, { useState, useCallback, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, TextInput } from 'react-native';
import { useFocusEffect, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import { apiGet, apiPost } from '../../src/api/client';
import { useToast } from '../../src/components/Toast';
import { useTheme } from '../../src/context/ThemeContext';
import { useNetwork } from '../../src/context/NetworkContext';
import type { ColorTheme } from '../../src/utils/theme';
import InlineConfirm from '../../src/components/InlineConfirm';
import RefundDialog from '../../src/components/RefundDialog';
import Card from '../../src/components/Card';
import ContentContainer from '../../src/components/ContentContainer';
import Input from '../../src/components/Input';
import Button from '../../src/components/Button';
import Picker from '../../src/components/Picker';
import DateTimePicker from '../../src/components/DateTimePicker';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import { rs } from '../../src/utils/responsive';
import { titleCase } from '../../src/utils/format';
import QrSvgView from '../../src/components/QrSvgView';
import VendingCompCard from '../../src/components/VendingCompCard';

interface AttendantProfile {
  readonly id: string;
  readonly isActive: boolean;
  readonly locations: {
    readonly locationId: string;
    readonly locationName: string;
    readonly role: string;
    readonly roleName?: string;
    readonly resolvedPermissions?: {
      timeclock: boolean;
      start_washes: boolean;
      emergency_contact: boolean;
      refund_mode: 'none' | 'direct' | 'request';
      view_hardware: boolean;
      issue_rewards?: boolean;
      equipment_buttons?: boolean;
      issue_rewards_daily_count?: number | null;
      issue_rewards_daily_cents?: number | null;
    };
  }[];
  readonly activeShift?: {
    readonly id: string;
    readonly locationId: string;
    readonly locationName: string;
    readonly clockIn: string;
    readonly notes?: string;
  };
}

/** Get resolved permissions for a specific location */
function getPermissions(profile: AttendantProfile, locationId?: string) {
  const loc = profile.locations.find(l => l.locationId === locationId) || profile.locations[0];
  return loc?.resolvedPermissions || {
    timeclock: true, start_washes: true, emergency_contact: false,
    refund_mode: 'none' as const, view_hardware: false,
  };
}

/** Get permissions for a specific location (used for per-transaction refund checks) */
function getLocationPermissions(profile: AttendantProfile, locationId: string) {
  const loc = profile.locations.find(l => l.locationId === locationId);
  return loc?.resolvedPermissions || {
    timeclock: false, start_washes: false, emergency_contact: false,
    refund_mode: 'none' as const, view_hardware: false,
  };
}

interface Shift {
  readonly id: string;
  readonly locationId: string;
  readonly locationName: string;
  readonly clockIn: string;
  readonly clockOut?: string;
  readonly duration?: number;
  readonly notes?: string;
}

interface WashTier {
  readonly name: string;
  readonly price: number;
  /** Vacuum packages are timed products; minutes come from durationMs. */
  readonly durationMs?: number;
  readonly enabled?: boolean;
}

interface Bay {
  readonly id: string;
  readonly name: string;
  readonly type: string;
  readonly controllerId: string | null;
  readonly tiersJson: string | null;
  /** Round 2 (S06): upsells sold at an automatic bay. */
  readonly upsells?: { actionId: string; name: string; priceCents: number }[];
}
interface EquipmentButton { locationId: string; equipmentKind: 'bay' | 'vending'; equipmentId: string; equipmentName: string; equipmentType: string; actionId: string; name: string }

interface RewardTemplate {
  readonly id: string;
  readonly name: string;
  readonly discountKind: 'percent' | 'free_wash' | 'free_minutes' | 'free_vend' | 'credit';
  readonly percentOff: number | null;
  readonly tierName: string | null;
  readonly freeMinutes: number | null;
  /** Not yet returned by the template-list endpoint; label falls back to a generic string. */
  readonly freeVendCents?: number | null;
  readonly creditCents?: number | null;
  readonly locationId: string | null;
  readonly locationName: string | null;
  readonly redemptionLimit: number;
  readonly expiresInDays: number;
}
interface GeneratedQr {
  readonly id: string;
  readonly code: string;
  readonly templateName: string;
  readonly redemptionLimit: number;
  readonly expiresAt: string | null;
  readonly qrSvg: string | null;
  /** Same URL the QR encodes — copyable so the attendant can text/email it. */
  readonly redeemUrl: string | null;
}

interface WashHistory {
  readonly id: string;
  readonly status: string;
  readonly startedAt: string;
  readonly endedAt: string | null;
  readonly durationMinutes: number | null;
  readonly bayName: string;
  readonly bayType: string;
  readonly locationName: string;
}

interface ActiveWash {
  readonly active: boolean;
  readonly sessionId?: string;
  readonly bayName?: string;
  readonly bayType?: string;
  readonly locationName?: string;
  readonly startedAt?: string;
}

/** AttendantDashboardScreen - Clock in/out, start/stop washes, shift history for attendants. */
export default function AttendantDashboardScreen() {
  const router = useRouter();
  const [profile, setProfile] = useState<AttendantProfile | null>(null);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [shiftsTotal, setShiftsTotal] = useState(0);
  const [shiftsPage, setShiftsPage] = useState(1);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  // Active location selector — single source of truth for all sections
  const [activeLocationId, setActiveLocationId] = useState('');

  // Clock out form
  const [notes, setNotes] = useState('');

  // Timer
  const [shiftDuration, setShiftDuration] = useState('');

  // Wash state
  const [activeWash, setActiveWash] = useState<ActiveWash | null>(null);
  const [locationBays, setLocationBays] = useState<Bay[]>([]);
  const [selectedBayId, setSelectedBayId] = useState('');
  const [startingWash, setStartingWash] = useState(false);
  const [stoppingWash, setStoppingWash] = useState(false);
  const [loadingBays, setLoadingBays] = useState(false);
  const [washDuration, setWashDuration] = useState('');
  const [selectedTier, setSelectedTier] = useState('');
  const [selectedUpsells, setSelectedUpsells] = useState<string[]>([]);
  const [equipButtons, setEquipButtons] = useState<EquipmentButton[]>([]);
  const [runningButton, setRunningButton] = useState<string | null>(null);
  const [washHistory, setWashHistory] = useState<WashHistory[]>([]);
  const [washHistoryTotal, setWashHistoryTotal] = useState(0);
  const [washHistoryPage, setWashHistoryPage] = useState(1);
  const [washHistoryLoading, setWashHistoryLoading] = useState(false);

  // Inline confirmations
  const [confirmStopWash, setConfirmStopWash] = useState(false);
  const [confirmClockOut, setConfirmClockOut] = useState(false);

  // Forgot-to-clock-out self-correction
  const [forgotMode, setForgotMode] = useState(false);
  const [actualLeftTime, setActualLeftTime] = useState('');

  // Transactions
  const [txns, setTxns] = useState<any[]>([]);
  const [txnPage, setTxnPage] = useState(1);
  const [txnSearch, setTxnSearch] = useState('');
  const txnSearchRef = useRef('');
  const [washSearch, setWashSearch] = useState('');
  const washSearchRef = useRef('');
  const [shiftsSearch, setShiftsSearch] = useState('');
  const shiftsSearchRef = useRef('');
  const [txnTotal, setTxnTotal] = useState(0);
  const [txnLoading, setTxnLoading] = useState(false);
  const [refundConfirmId, setRefundConfirmId] = useState<string | null>(null);
  const [refundProcessing, setRefundProcessing] = useState(false);

  // Emergency contact
  const [emergencyExpanded, setEmergencyExpanded] = useState(false);
  const [emergencyMsg, setEmergencyMsg] = useState('');
  const [sendingEmergency, setSendingEmergency] = useState(false);

  // Reward QR generation from owner templates — permission-gated
  const [rewardExpanded, setRewardExpanded] = useState(false);
  const [rewardTemplates, setRewardTemplates] = useState<RewardTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [generatedQr, setGeneratedQr] = useState<GeneratedQr | null>(null);

  // Hardware status
  const [controllerStatuses, setControllerStatuses] = useState<any[]>([]);
  const [loadingControllers, setLoadingControllers] = useState(false);

  const toast = useToast();
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const { isOffline } = useNetwork();

  /** Fetch attendant profile, shifts, active wash, and wash history in parallel. */
  const fetchData = async () => {
    let historyFailed = false;
    const emptyHistory = { washes: [] as WashHistory[], total: 0 };
    try {
      const [profileData, shiftsData, washData, washHistoryData] = await Promise.all([
        apiGet<AttendantProfile>('/api/attendants/me'),
        apiGet<{ shifts: Shift[]; total: number }>('/api/attendants/shifts?page=1&limit=10'),
        apiGet<ActiveWash>('/api/attendants/wash/active'),
        apiGet<{ washes: WashHistory[]; total: number }>('/api/attendants/wash/history?page=1&limit=10').catch(() => { historyFailed = true; return emptyHistory; }),
      ]);

      setProfile(profileData);
      setShifts(shiftsData.shifts);
      setShiftsTotal(shiftsData.total);
      setShiftsPage(1);
      setActiveWash(washData);
      setWashHistory(washHistoryData.washes);
      setWashHistoryTotal(washHistoryData.total);
      setWashHistoryPage(1);
      if (profileData.activeShift?.notes) setNotes(profileData.activeShift.notes);

      // Auto-select: shift location if clocked in, else first location
      if (profileData.locations.length > 0 && !activeLocationId) {
        setActiveLocationId(profileData.activeShift?.locationId || profileData.locations[0].locationId);
      }

      // Fetch controller status for active location if it has view_hardware permission.
      // Silent on failure: it's a best-effort secondary indicator alongside the main wash UI.
      const activeLoc = profileData.locations.find(l => l.locationId === (profileData.activeShift?.locationId || profileData.locations[0]?.locationId));
      if (activeLoc?.resolvedPermissions?.view_hardware) {
        apiGet<any[]>(`/api/attendants/locations/${activeLoc.locationId}/controllers`).catch(() => [])
          .then(results => setControllerStatuses(results));
      } else {
        setControllerStatuses([]);
      }
      if (historyFailed && !isOffline) {
        toast.show({ type: 'warning', title: 'Wash history didn’t load' });
      }
    } catch (err: any) {
      if (err.message.includes('404')) {
        toast.show({
          type: 'info',
          title: 'Not an Attendant',
          message: 'You are not registered as an attendant'
        });
      } else {
        toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    } finally {
      setLoading(false);
    }
  };

  useFocusEffect(useCallback(() => {
    fetchData();
  }, []));

  const MS_PER_HOUR = 1000 * 60 * 60;
  const MS_PER_MINUTE = 1000 * 60;
  const MS_PER_SECOND = 1000;
  const TIMER_INTERVAL_MS = 1000;

  // Update shift duration timer every second when shift is active
  useEffect(() => {
    if (!profile?.activeShift) {
      setShiftDuration('');
      return;
    }

    const updateDuration = () => {
      const start = new Date(profile.activeShift!.clockIn).getTime();
      const now = Date.now();
      const durationMs = now - start;

      const hours = Math.floor(durationMs / MS_PER_HOUR);
      const minutes = Math.floor((durationMs % MS_PER_HOUR) / MS_PER_MINUTE);
      const seconds = Math.floor((durationMs % MS_PER_MINUTE) / MS_PER_SECOND);

      setShiftDuration(`${hours}h ${minutes}m ${seconds}s`);
    };

    updateDuration();
    const interval = setInterval(updateDuration, TIMER_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [profile?.activeShift]);

  // Wash timer — updates elapsed wash time every second
  useEffect(() => {
    if (!activeWash?.active || !activeWash.startedAt) {
      setWashDuration('');
      return;
    }

    let capRefetched = false;
    const updateWashDuration = () => {
      const start = new Date(activeWash.startedAt!).getTime();
      const now = Date.now();
      // Comp washes auto-end at 15 min on the backend; freeze the display there
      // and refetch once (after the backend sweep has run) to clear the card.
      const ms = Math.min(now - start, 15 * MS_PER_MINUTE);
      if (now - start >= 15 * MS_PER_MINUTE + 30 * MS_PER_SECOND && !capRefetched) {
        capRefetched = true;
        fetchData();
      }
      const hours = Math.floor(ms / MS_PER_HOUR);
      const minutes = Math.floor((ms % MS_PER_HOUR) / MS_PER_MINUTE);
      const seconds = Math.floor((ms % MS_PER_MINUTE) / MS_PER_SECOND);
      setWashDuration(`${hours}h ${minutes}m ${seconds}s`);
    };

    updateWashDuration();
    const interval = setInterval(updateWashDuration, TIMER_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [activeWash?.active, activeWash?.startedAt]);

  // Fetch bays when active location changes
  useEffect(() => {
    const ac = new AbortController();
    if (!activeLocationId) {
      setLocationBays([]);
      setSelectedBayId('');
      return () => ac.abort();
    }

    setLoadingBays(true);
    // Round 2 (S06): Equipment buttons for this location (403 without the permission → empty list).
    apiGet<{ buttons: EquipmentButton[] }>(`/api/attendants/locations/${activeLocationId}/equipment-buttons`)
      .then(r => { if (!ac.signal.aborted) setEquipButtons(r.buttons ?? []); })
      .catch(() => { if (!ac.signal.aborted) setEquipButtons([]); });
    apiGet<Bay[]>(`/api/attendants/locations/${activeLocationId}/bays`)
      .then(data => {
        if (ac.signal.aborted) return;
        setLocationBays(data);
        setSelectedBayId(data.length > 0 ? data[0].id : '');
      })
      .catch(() => {
        if (ac.signal.aborted) return;
        setLocationBays([]);
        setSelectedBayId('');
      })
      .finally(() => { if (!ac.signal.aborted) setLoadingBays(false); });
    return () => ac.abort();
  }, [activeLocationId]);

  // Re-fetch transactions and controllers when active location or profile changes
  useEffect(() => {
    if (!activeLocationId || !profile) return;
    fetchTransactions(1);
    const locPerms = getPermissions(profile, activeLocationId);
    if (locPerms.view_hardware) {
      apiGet<any[]>(`/api/attendants/locations/${activeLocationId}/controllers`).catch(() => [])
        .then(results => setControllerStatuses(results));
    } else {
      setControllerStatuses([]);
    }
  }, [activeLocationId, profile]);

  const selectedBay = locationBays.find(b => b.id === selectedBayId);
  // Automatic bays: wash options. Package vacuums: timed packages — the comp
  // start REQUIRES tierName for those (mig 0064; the 400 returns {tiers}, but
  // we read tiersJson up front instead). Metered vacs (no packages) run
  // count-up like self-serve.
  const isVacBay = selectedBay?.type === 'vacuum';
  const bayTiers: WashTier[] = selectedBay?.tiersJson
    ? selectedBay.type === 'automatic'
      ? JSON.parse(selectedBay.tiersJson)
      : isVacBay
        ? (JSON.parse(selectedBay.tiersJson) as WashTier[]).filter(t => t.enabled !== false && t.durationMs)
        : []
    : [];

  /** POST /api/attendants/wash/start — activate wash equipment for the selected bay. */
  const handleStartWash = async () => {
    if (!selectedBayId) {
      toast.show({ type: 'error', title: 'Error', message: 'Please select a bay' });
      return;
    }
    if (bayTiers.length > 0 && !selectedTier) {
      toast.show({ type: 'error', title: 'Error', message: isVacBay ? 'Please select a vacuum package' : 'Please select a wash option' });
      return;
    }

    setStartingWash(true);
    try {
      const body: any = { bayId: selectedBayId };
      if (selectedTier) body.tierName = selectedTier;
      if (selectedUpsells.length) body.upsellActionIds = selectedUpsells;
      await apiPost('/api/attendants/wash/start', body);
      toast.show({ type: 'success', title: isVacBay ? 'Vacuum Started' : 'Wash Started', message: 'Equipment activated' });
      setSelectedTier(''); setSelectedUpsells([]);
      fetchData();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Start Failed', message: err.message });
    } finally {
      setStartingWash(false);
    }
  };

  const handleStopWash = () => {
    if (!activeWash?.sessionId) return;
    setConfirmStopWash(true);
  };

  const doStopWash = async () => {
    if (!activeWash?.sessionId) return;
    setStoppingWash(true);
    try {
      await apiPost('/api/attendants/wash/stop', { sessionId: activeWash.sessionId });
      toast.show({ type: 'success', title: 'Wash Stopped', message: 'Equipment deactivated' });
      setConfirmStopWash(false);
      fetchData();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Stop Failed', message: err.message });
    } finally {
      setStoppingWash(false);
    }
  };

  /** POST /api/attendants/clock-in — start a shift at the active location. */
  const handleClockIn = async () => {
    if (!activeLocationId) {
      toast.show({ type: 'error', title: 'Error', message: 'Please select a location' });
      return;
    }

    setClockingIn(true);
    try {
      await apiPost('/api/attendants/clock-in', {
        locationId: activeLocationId,
      });

      toast.show({ type: 'success', title: 'Clocked In', message: 'Your shift has started' });
      fetchData();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Clock In Failed', message: err.message });
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = () => {
    if (!profile?.activeShift) return;
    setConfirmClockOut(true);
  };

  const doClockOut = async () => {
    setClockingOut(true);
    try {
      let actualClockOutTime: string | undefined;
      if (forgotMode && actualLeftTime) {
        const parsed = new Date(actualLeftTime);
        if (isNaN(parsed.getTime())) {
          toast.show({ type: 'error', title: 'Invalid time', message: 'Enter a valid date/time.' });
          setClockingOut(false);
          return;
        }
        actualClockOutTime = parsed.toISOString();
      }

      const res: { selfCorrected?: boolean } = await apiPost('/api/attendants/clock-out', {
        shiftId: profile!.activeShift!.id,
        notes: notes.trim() || undefined,
        actualClockOutTime,
      });

      toast.show({
        type: 'success',
        title: 'Clocked Out',
        message: res.selfCorrected ? 'Shift ended at the time you entered — your manager has been notified.' : 'Your shift has ended',
      });
      setNotes('');
      setForgotMode(false);
      setActualLeftTime('');
      setConfirmClockOut(false);
      fetchData();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Clock Out Failed', message: err.message });
    } finally {
      setClockingOut(false);
    }
  };

  /** POST /api/attendants/shift/notes — save notes without clocking out. */
  const handleSaveNotes = async () => {
    if (!profile?.activeShift) return;
    setSavingNotes(true);
    try {
      await apiPost('/api/attendants/shift/notes', {
        shiftId: profile.activeShift.id,
        notes: notes.trim(),
      });
      toast.show({ type: 'success', title: 'Notes saved' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Save Failed', message: err.message });
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSendEmergency = async () => {
    if (!emergencyMsg.trim()) return;
    setSendingEmergency(true);
    try {
      const locId = activeLocationId || profile?.activeShift?.locationId || profile?.locations[0]?.locationId;
      await apiPost('/api/attendants/emergency-contact', { message: emergencyMsg.trim(), locationId: locId });
      toast.show({ type: 'success', title: 'Sent', message: 'Emergency message sent to owner' });
      setEmergencyMsg('');
      setEmergencyExpanded(false);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Failed', message: err.message });
    } finally {
      setSendingEmergency(false);
    }
  };

  const fetchRewardTemplates = async () => {
    const locId = activeLocationId || profile?.activeShift?.locationId || profile?.locations[0]?.locationId;
    if (!locId) return;
    setLoadingTemplates(true);
    try {
      const data = await apiGet<{ templates: RewardTemplate[] }>(`/api/attendants/rewards/templates?locationId=${locId}`);
      setRewardTemplates(data.templates);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setLoadingTemplates(false); }
  };

  const handleGenerateQr = async (tpl: RewardTemplate) => {
    const locId = activeLocationId || profile?.activeShift?.locationId || profile?.locations[0]?.locationId;
    if (!locId) return;
    setGeneratingId(tpl.id);
    try {
      const res = await apiPost<GeneratedQr>('/api/attendants/rewards/from-template', {
        locationId: locId, templateId: tpl.id,
      });
      setGeneratedQr(res);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Failed', message: err.message });
    } finally { setGeneratingId(null); }
  };

  /** Copy the redeem link so the attendant can paste it into a text/email. */
  const handleCopyRedeemLink = async () => {
    if (!generatedQr?.redeemUrl) return;
    try {
      await Clipboard.setStringAsync(generatedQr.redeemUrl);
      toast.show({ type: 'success', title: 'Link copied', message: 'Paste it into a text or email to the customer.' });
    } catch {
      toast.show({ type: 'error', title: 'Copy failed', message: generatedQr.redeemUrl });
    }
  };

  /** Human offer description for a template row. */
  const templateOffer = (t: RewardTemplate): string => {
    let offer: string;
    if (t.discountKind === 'free_vend') offer = t.freeVendCents != null ? `$${(t.freeVendCents / 100).toFixed(2)} vending credit` : 'Vending credit';
    else if (t.discountKind === 'credit') offer = t.creditCents != null ? `$${(t.creditCents / 100).toFixed(2)} gift credit` : 'Gift credit';
    else if (t.discountKind === 'free_minutes') offer = `${t.freeMinutes ?? '?'} free minutes`;
    else if (t.discountKind === 'free_wash' || t.percentOff === 100) offer = t.tierName ? `Free ${t.tierName} wash` : 'Free wash';
    else offer = `${t.percentOff}% off${t.tierName ? ` ${t.tierName}` : ''}`;
    const scope = t.locationName ? `at ${t.locationName}` : 'all locations';
    return `${offer} · ${scope} · single use · expires in ${t.expiresInDays}d`;
  };

  /** Fetch transactions for active location. */
  const fetchTransactions = async (page = 1) => {
    setTxnLoading(true);
    try {
      const locParam = activeLocationId ? `&locationId=${activeLocationId}` : '';
      const searchParam = txnSearchRef.current.trim() ? `&search=${encodeURIComponent(txnSearchRef.current.trim())}` : '';
      const data = await apiGet<{ transactions: any[]; total: number }>(`/api/attendants/transactions?page=${page}&limit=10${locParam}${searchParam}`);
      setTxns(data.transactions);
      setTxnTotal(data.total);
      setTxnPage(page);
    } catch { /* ignore */ }
    finally { setTxnLoading(false); }
  };

  /** Fetch a page of the attendant's wash history. */
  const fetchWashHistory = async (page = 1) => {
    setWashHistoryLoading(true);
    try {
      const searchParam = washSearchRef.current.trim() ? `&search=${encodeURIComponent(washSearchRef.current.trim())}` : '';
      const data = await apiGet<{ washes: WashHistory[]; total: number }>(`/api/attendants/wash/history?page=${page}&limit=10${searchParam}`);
      setWashHistory(data.washes);
      setWashHistoryTotal(data.total);
      setWashHistoryPage(page);
    } catch { /* ignore */ }
    finally { setWashHistoryLoading(false); }
  };

  /** Fetch a page of the attendant's shift history. */
  const fetchShifts = async (page = 1) => {
    setShiftsLoading(true);
    try {
      const searchParam = shiftsSearchRef.current.trim() ? `&search=${encodeURIComponent(shiftsSearchRef.current.trim())}` : '';
      const data = await apiGet<{ shifts: Shift[]; total: number }>(`/api/attendants/shifts?page=${page}&limit=10${searchParam}`);
      setShifts(data.shifts);
      setShiftsTotal(data.total);
      setShiftsPage(page);
    } catch { /* ignore */ }
    finally { setShiftsLoading(false); }
  };

  /** Compact search row used by the history/transactions/timecards boxes. */
  const renderBoxSearch = (
    value: string,
    setValue: (v: string) => void,
    ref: React.MutableRefObject<string>,
    onSubmit: () => void,
  ) => (
    <View style={styles.boxSearchRow}>
      <Ionicons name="search" size={16} color={colors.textLight} />
      <TextInput
        style={styles.boxSearchInput}
        placeholder="Search..."
        placeholderTextColor={colors.textLight}
        value={value}
        onChangeText={t => { ref.current = t; setValue(t); }}
        onSubmitEditing={onSubmit}
        returnKeyType="search"
        autoCapitalize="none"
        autoCorrect={false}
        maxLength={100}
      />
      {value.length > 0 && (
        <TouchableOpacity onPress={() => { ref.current = ''; setValue(''); onSubmit(); }} hitSlop={8}>
          <Ionicons name="close-circle" size={16} color={colors.textLight} />
        </TouchableOpacity>
      )}
    </View>
  );

  /** Handle refund or refund request for a session.
   *  Amount is in cents and may be partial; pass the txn's totalCharged for a full refund. */
  const handleRefund = async (sessionId: string, mode: 'direct' | 'request', amount: number, reason: string) => {
    if (!reason.trim()) {
      toast.show({ type: 'error', title: 'Error', message: 'Please enter a reason' });
      return;
    }
    setRefundProcessing(true);
    try {
      const endpoint = mode === 'direct' ? '/api/attendants/refund' : '/api/attendants/refund-request';
      await apiPost(endpoint, { washSessionId: sessionId, reason: reason.trim(), amount });
      toast.show({ type: 'success', title: mode === 'direct' ? 'Refunded' : 'Request Sent', message: mode === 'direct' ? 'Refund processed' : 'Refund request submitted' });
      setRefundConfirmId(null);
      fetchTransactions(txnPage);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Failed', message: err.message });
    } finally {
      setRefundProcessing(false);
    }
  };

  /** Format ISO string to short human-readable date/time. */
  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  /** Convert total minutes to "Xh Ym" format. */
  const formatDuration = (minutes: number) => {
    const MINUTES_PER_HOUR = 60;
    const hours = Math.floor(minutes / MINUTES_PER_HOUR);
    const mins = minutes % MINUTES_PER_HOUR;
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (!profile) {
    return (
      <View style={styles.errorContainer}>
        <Ionicons name="information-circle" size={64} color={colors.textLight} />
        <Text style={styles.errorTitle}>Not an Attendant</Text>
        <Text style={styles.errorText}>
          You are not registered as an attendant. Contact your location owner to get added.
        </Text>
      </View>
    );
  }

  if (!profile.isActive) {
    return (
      <View style={styles.errorContainer}>
        <Ionicons name="lock-closed" size={64} color={colors.warning} />
        <Text style={styles.errorTitle}>Account Inactive</Text>
        <Text style={styles.errorText}>
          Your attendant account is currently inactive. Contact your location owner for assistance.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
      <ContentContainer>
      {(() => {
        const permissions = getPermissions(profile, activeLocationId);
        return (<>

      {/* Working Location Selector */}
      {profile.locations.length > 1 && (
        <Card style={{ marginBottom: spacing.md }}>
          <Picker
            label="Working Location"
            options={profile.locations.map(loc => ({
              label: `${loc.locationName} (${loc.roleName || loc.role})`,
              value: loc.locationId,
            }))}
            value={activeLocationId}
            onValueChange={(val: string) => setActiveLocationId(val)}
          />
        </Card>
      )}

      {/* Manage Team shortcut */}
      {(permissions as any).manage_team && (
        <Card style={{ marginBottom: spacing.md }}>
          <Button
            title="Manage Team"
            onPress={() => router.push('/user/team' as never)}
            icon={<Ionicons name="people" size={18} color={colors.white} />}
            size="sm"
          />
        </Card>
      )}

      {/* Emergency Contact */}
      {permissions.emergency_contact && (
        <Card style={styles.emergencyCard}>
          <TouchableOpacity style={styles.emergencyHeader} onPress={() => setEmergencyExpanded(!emergencyExpanded)}>
            <Ionicons name="warning" size={24} color={colors.danger} />
            <Text style={styles.emergencyTitle}>Emergency Contact</Text>
            <Ionicons name={emergencyExpanded ? 'chevron-up' : 'chevron-down'} size={20} color={colors.textSecondary} />
          </TouchableOpacity>
          {emergencyExpanded && (
            <View style={styles.emergencyBody}>
              <Input
                placeholder="Describe the emergency..."
                value={emergencyMsg}
                onChangeText={setEmergencyMsg}
                multiline
                numberOfLines={3}
                maxLength={1000}
              />
              <Button
                title="Send to Owner"
                onPress={handleSendEmergency}
                loading={sendingEmergency}
                disabled={!emergencyMsg.trim()}
                variant="danger"
                size="sm"
                icon={<Ionicons name="send" size={16} color={colors.white} />}
                style={{ marginTop: spacing.sm }}
              />
            </View>
          )}
        </Card>
      )}

      {/* Reward QRs — generated from owner-defined templates */}
      {(permissions as any).issue_rewards && (
        <Card style={{ marginBottom: spacing.md }}>
          <TouchableOpacity
            style={styles.emergencyHeader}
            onPress={() => {
              const next = !rewardExpanded;
              setRewardExpanded(next);
              if (next && rewardTemplates.length === 0) fetchRewardTemplates();
            }}
          >
            <Ionicons name="qr-code" size={24} color={colors.success} />
            <Text style={[styles.emergencyTitle, { color: colors.text }]}>Reward QR Codes</Text>
            <Ionicons name={rewardExpanded ? 'chevron-up' : 'chevron-down'} size={20} color={colors.textSecondary} />
          </TouchableOpacity>
          {rewardExpanded && (
            <View style={styles.emergencyBody}>
              {loadingTemplates ? (
                <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.md }} />
              ) : rewardTemplates.length === 0 ? (
                <Text style={{ fontSize: fontSize.sm, color: colors.textSecondary, paddingVertical: spacing.sm }}>
                  No reward templates yet — ask the owner to create some under Staff → QR Reward Templates.
                </Text>
              ) : (
                rewardTemplates.map(tpl => (
                  <View key={tpl.id} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm, borderTopWidth: 1, borderTopColor: colors.borderLight, gap: spacing.sm }}>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold as any, color: colors.text }}>{tpl.name}</Text>
                      <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: 2 }}>{templateOffer(tpl)}</Text>
                    </View>
                    <Button
                      title="Generate"
                      size="sm"
                      loading={generatingId === tpl.id}
                      onPress={() => handleGenerateQr(tpl)}
                      icon={<Ionicons name="qr-code-outline" size={14} color={colors.white} />}
                    />
                  </View>
                ))
              )}
            </View>
          )}
        </Card>
      )}

      {/* Generated QR display */}
      {generatedQr && (
        <Card style={{ marginBottom: spacing.md, alignItems: 'center' }}>
          <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.bold as any, color: colors.text }}>{generatedQr.templateName}</Text>
          <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: 2 }}>
            Code {generatedQr.code} · {generatedQr.redemptionLimit > 1 ? `${generatedQr.redemptionLimit} uses` : 'single use'}
            {generatedQr.expiresAt ? ` · expires ${new Date(generatedQr.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
          </Text>
          {generatedQr.qrSvg && (
            <View style={{ marginTop: spacing.md, width: '100%', maxWidth: 320, alignSelf: 'center' }}>
              <QrSvgView uri={generatedQr.qrSvg} width={300} accessibilityLabel="Reward QR code" />
            </View>
          )}
          <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: spacing.sm, textAlign: 'center' }}>
            Have the customer scan this — or give them the code to enter in their app.
          </Text>
          <View style={{ flexDirection: 'row', gap: spacing.sm, marginTop: spacing.md }}>
            {generatedQr.redeemUrl ? (
              <Button
                title="Copy Link"
                variant="outline"
                size="sm"
                icon={<Ionicons name="link" size={16} color={colors.primary} />}
                onPress={handleCopyRedeemLink}
              />
            ) : null}
            <Button title="Done" variant="outline" size="sm" onPress={() => setGeneratedQr(null)} />
          </View>
        </Card>
      )}

      {/* Active Shift or Clock In */}
      {(permissions.timeclock || profile.activeShift) && (profile.activeShift ? (
        <Card style={styles.activeShiftCard}>
          <View style={styles.activeShiftHeader}>
            <View style={styles.pulseDotLarge} />
            <View style={styles.activeShiftInfo}>
              <Text style={styles.activeShiftTitle}>Active Shift</Text>
              <Text style={styles.activeShiftLocation}>{profile.activeShift.locationName}</Text>
            </View>
          </View>

          <View style={styles.timerContainer}>
            <Text style={styles.timerLabel}>Time Elapsed</Text>
            <Text style={styles.timerValue}>{shiftDuration}</Text>
          </View>

          <Text style={styles.clockInTime}>
            Started: {formatDateTime(profile.activeShift.clockIn)}
          </Text>

          <Input
            label="Notes (optional)"
            placeholder="Add any notes about this shift..."
            value={notes}
            onChangeText={setNotes}
            multiline
            numberOfLines={3}
            maxLength={5000}
            style={styles.notesInput}
          />

          <Button
            title="Save Notes"
            onPress={handleSaveNotes}
            loading={savingNotes}
            disabled={!notes.trim()}
            variant="outline"
            size="sm"
            icon={<Ionicons name="save-outline" size={18} color={colors.primary} />}
          />

          <TouchableOpacity
            onPress={() => setForgotMode(v => !v)}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: spacing.sm }}
          >
            <Ionicons name={forgotMode ? 'checkbox' : 'square-outline'} size={20} color={colors.primary} />
            <Text style={{ fontSize: fontSize.sm, color: colors.primary }}>I forgot to clock out — set actual time</Text>
          </TouchableOpacity>

          {forgotMode && (
            <View style={{ marginTop: spacing.sm }}>
              <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: 4 }}>
                When did you actually leave?
              </Text>
              <DateTimePicker value={actualLeftTime} onChange={setActualLeftTime} />
              <Text style={{ fontSize: fontSize.xs, color: colors.textLight, marginTop: 4 }}>
                Your manager will be notified that you self-corrected this clock-out.
              </Text>
            </View>
          )}

          <Button
            title="Clock Out"
            onPress={handleClockOut}
            loading={clockingOut}
            variant="danger"
            size="lg"
            icon={<Ionicons name="log-out" size={22} color={colors.white} />}
            style={styles.clockButton}
          />

          <InlineConfirm
            message={forgotMode && actualLeftTime ? `Clock out at ${actualLeftTime}? Your manager will be notified.` : 'End your shift now?'}
            visible={confirmClockOut}
            onConfirm={doClockOut}
            onCancel={() => setConfirmClockOut(false)}
            confirmLabel="Clock Out"
            loading={clockingOut}
            variant="danger"
          />
        </Card>
      ) : (
        <Card style={styles.clockInCard}>
          <View style={styles.clockInHeader}>
            <Ionicons name="time" size={48} color={colors.primary} />
            <Text style={styles.clockInTitle}>Clock In</Text>
            <Text style={styles.clockInSubtitle}>Start your shift{activeLocationId && profile.locations.length > 0 ? ` at ${profile.locations.find(l => l.locationId === activeLocationId)?.locationName || ''}` : ''}</Text>
          </View>

          {profile.locations.length > 0 ? (
              <Button
                title="Clock In"
                onPress={handleClockIn}
                loading={clockingIn}
                disabled={!activeLocationId}
                size="lg"
                icon={<Ionicons name="log-in" size={22} color={colors.white} />}
                style={styles.clockButton}
              />
          ) : (
            <View style={styles.noLocations}>
              <Text style={styles.noLocationsText}>
                You are not assigned to any locations yet
              </Text>
            </View>
          )}
        </Card>
      ))}

      {/* Wash Controls */}
      {permissions.start_washes && (activeWash?.active ? (
        <Card style={styles.activeWashCard}>
          <View style={styles.activeShiftHeader}>
            <View style={[styles.pulseDotLarge, { backgroundColor: colors.primary }]} />
            <View style={styles.activeShiftInfo}>
              <Text style={[styles.activeShiftTitle, { color: colors.primary }]}>
                {activeWash.bayType === 'vacuum' ? 'Active Vacuum' : 'Active Wash'}
              </Text>
              <Text style={styles.activeShiftLocation}>
                {activeWash.bayName} — {activeWash.locationName}
              </Text>
            </View>
          </View>

          <View style={styles.timerContainer}>
            <Text style={styles.timerLabel}>{activeWash.bayType === 'vacuum' ? 'Vacuum Time' : 'Wash Time'}</Text>
            <Text style={[styles.timerValue, { color: colors.primary }]}>{washDuration}</Text>
          </View>

          {activeWash.bayType !== 'automatic' && (
            <>
              <Button
                title={activeWash.bayType === 'vacuum' ? 'Stop Vacuum' : 'Stop Wash'}
                onPress={handleStopWash}
                loading={stoppingWash}
                variant="danger"
                size="lg"
                icon={<Ionicons name="stop-circle" size={22} color={colors.white} />}
                style={styles.clockButton}
              />

              <InlineConfirm
                message={activeWash.bayType === 'vacuum' ? 'Stop the vacuum and deactivate equipment?' : 'Stop the wash and deactivate equipment?'}
                visible={confirmStopWash}
                onConfirm={doStopWash}
                onCancel={() => setConfirmStopWash(false)}
                confirmLabel="Stop Wash"
                loading={stoppingWash}
                variant="warning"
              />
            </>
          )}
        </Card>
      ) : (
        <Card style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Start Wash</Text>
          <Text style={styles.washSubtitle}>Activate a complimentary wash at any assigned bay</Text>

          {profile.locations.length > 0 ? (
            <>
              {loadingBays ? (
                <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.md }} />
              ) : locationBays.length > 0 ? (
                <Picker
                  label="Bay"
                  options={locationBays.map(b => ({
                    label: `${b.name} (${b.type})`,
                    value: b.id,
                  }))}
                  value={selectedBayId}
                  onValueChange={(val: string) => { setSelectedBayId(val); setSelectedTier(''); }}
                />
              ) : activeLocationId ? (
                <Text style={styles.emptyText}>No active bays at this location</Text>
              ) : null}

              {bayTiers.length > 0 && (
                <View style={styles.tierSection}>
                  <Text style={styles.tierLabel}>{isVacBay ? 'Select Vacuum Package' : 'Select Wash'}</Text>
                  {bayTiers.map(tier => (
                    <TouchableOpacity
                      key={tier.name}
                      style={[styles.tierOption, selectedTier === tier.name && styles.tierOptionSelected]}
                      onPress={() => setSelectedTier(tier.name)}
                    >
                      <View style={styles.tierRadio}>
                        {selectedTier === tier.name && <View style={styles.tierRadioDot} />}
                      </View>
                      <Text style={[styles.tierName, selectedTier === tier.name && styles.tierNameSelected]}>
                        {tier.name}{isVacBay && tier.durationMs ? ` (${Math.round(tier.durationMs / 60000)} min)` : ''}
                      </Text>
                      <Text style={[styles.tierPrice, selectedTier === tier.name && styles.tierPriceSelected]}>
                        ${(tier.price / 100).toFixed(2)}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              )}
              {selectedBay?.type === 'automatic' && (selectedBay.upsells?.length ?? 0) > 0 && (
                <View style={styles.tierSection}>
                  <Text style={styles.tierLabel}>Upsells (free with this comp)</Text>
                  {selectedBay.upsells!.map(u => { const on = selectedUpsells.includes(u.actionId); return (
                    <TouchableOpacity key={u.actionId} style={[styles.tierOption, on && styles.tierOptionSelected]} onPress={() => setSelectedUpsells(on ? selectedUpsells.filter(x => x !== u.actionId) : [...selectedUpsells, u.actionId])}>
                      <Ionicons name={on ? 'checkbox' : 'square-outline'} size={20} color={on ? colors.primary : colors.textSecondary} />
                      <Text style={[styles.tierName, on && styles.tierNameSelected]}>{u.name}</Text>
                    </TouchableOpacity>
                  ); })}
                </View>
              )}
              {/* Metered vacs run count-up like self-serve — no package needed. */}
              {isVacBay && bayTiers.length === 0 && (
                <Text style={[styles.washSubtitle, { marginTop: spacing.xs }]}>
                  Metered vacuum — starts count-up, stop it when done.
                </Text>
              )}

              <Button
                title={isVacBay ? 'Start Vacuum' : 'Start Wash'}
                onPress={handleStartWash}
                loading={startingWash}
                disabled={!selectedBayId || (bayTiers.length > 0 && !selectedTier)}
                size="lg"
                icon={<Ionicons name={isVacBay ? 'sparkles' : 'water'} size={22} color={colors.white} />}
                style={styles.clockButton}
              />
            </>
          ) : (
            <Text style={styles.emptyText}>No locations assigned</Text>
          )}
        </Card>
      ))}

      {/* Vending — complimentary credit to a machine at the working location */}
      {permissions.start_washes && (
        <View style={{ marginTop: spacing.md }}>
          <VendingCompCard locationId={activeLocationId || null} compEndpoint="/api/attendants/vending/comp" />
        </View>
      )}

      {/* Wash History */}
      {permissions.start_washes && (washHistory.length > 0 || washHistoryTotal > 0 || washSearch !== '') && (
        <Card style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Recent Washes ({washHistoryTotal})</Text>
          {renderBoxSearch(washSearch, setWashSearch, washSearchRef, () => fetchWashHistory(1))}
          {washHistory.length === 0 && <Text style={styles.emptyText}>No washes match.</Text>}
          {washHistory.map(w => (
            <View key={w.id} style={styles.washHistoryItem}>
              <View style={styles.washHistoryIcon}>
                <Ionicons
                  name={w.bayType === 'automatic' ? 'car-sport' : w.bayType === 'vacuum' ? 'sparkles' : 'water'}
                  size={20}
                  color={w.status === 'active' ? colors.primary : colors.success}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.washHistoryTitle}>
                  {w.bayName}
                </Text>
                <Text style={styles.washHistoryDetail}>
                  {w.locationName} · {new Date(w.startedAt).toLocaleString('en-US', {
                    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                  })}
                </Text>
              </View>
              <View style={[
                styles.washHistoryBadge,
                { backgroundColor: (w.status === 'active' ? colors.primary : colors.success) + '20' },
              ]}>
                <Text style={[
                  styles.washHistoryBadgeText,
                  { color: w.status === 'active' ? colors.primary : colors.success },
                ]}>
                  {w.status === 'active' ? 'Active' : w.durationMinutes != null ? `${Math.round(w.durationMinutes)}m` : 'Done'}
                </Text>
              </View>
            </View>
          ))}
          {washHistoryTotal > 10 && (
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: spacing.sm }}>
              <Button
                title="Previous"
                onPress={() => fetchWashHistory(washHistoryPage - 1)}
                disabled={washHistoryPage <= 1 || washHistoryLoading}
                size="sm"
                variant="outline"
              />
              <Text style={styles.locationRole}>Page {washHistoryPage} of {Math.ceil(washHistoryTotal / 10)}</Text>
              <Button
                title="Next"
                onPress={() => fetchWashHistory(washHistoryPage + 1)}
                disabled={washHistoryPage >= Math.ceil(washHistoryTotal / 10) || washHistoryLoading}
                size="sm"
                variant="outline"
              />
            </View>
          )}
        </Card>
      )}

      {/* Transactions */}
      {permissions.refund_mode !== 'none' && (
        <Card style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Transactions</Text>
          {renderBoxSearch(txnSearch, setTxnSearch, txnSearchRef, () => fetchTransactions(1))}
          {txnLoading && txns.length === 0 ? (
            <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.md }} />
          ) : txns.length === 0 ? (
            <Text style={styles.emptyText}>No transactions</Text>
          ) : (
            <>
              {txns.map(txn => (
                <View key={txn.id} style={styles.locationItem}>
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text style={styles.locationName}>{titleCase(txn.customerName)}</Text>
                      <Text style={[styles.locationName, { color: colors.success }]}>
                        ${((txn.totalCharged || 0) / 100).toFixed(2)}
                      </Text>
                    </View>
                    <Text style={styles.locationRole}>
                      {txn.bayName} · {txn.locationName}{txn.paymentLast4 ? ` · •••${txn.paymentLast4}` : ''} · {new Date(txn.startedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </Text>
                    {txn.status === 'refunded' && (
                      <View style={[styles.washHistoryBadge, { backgroundColor: colors.warning + '20', marginTop: 4, alignSelf: 'flex-start' }]}>
                        <Text style={[styles.washHistoryBadgeText, { color: colors.warning }]}>Refunded</Text>
                      </View>
                    )}
                    {txn.status === 'partially_refunded' && (
                      <View style={[styles.washHistoryBadge, { backgroundColor: colors.warning + '20', marginTop: 4, alignSelf: 'flex-start' }]}>
                        <Text style={[styles.washHistoryBadgeText, { color: colors.warning }]}>
                          ${((txn.refundedAmount || 0) / 100).toFixed(2)} refunded
                        </Text>
                      </View>
                    )}
                    {refundConfirmId === txn.id ? (
                      (() => {
                        const mode = (txn.locationId && profile ? getLocationPermissions(profile, txn.locationId).refund_mode : permissions.refund_mode) as 'direct' | 'request';
                        return (
                          <RefundDialog
                            visible={true}
                            originalCents={txn.totalCharged || 0}
                            alreadyRefundedCents={txn.refundedAmount || 0}
                            requireReason
                            confirmLabel={mode === 'direct' ? 'Refund' : 'Request'}
                            title={mode === 'direct' ? 'Issue refund' : 'Request refund'}
                            subtitle={mode === 'direct' ? 'Refund to customer' : 'Submit refund request to owner'}
                            loading={refundProcessing}
                            onCancel={() => setRefundConfirmId(null)}
                            onConfirm={(amount, reason) => handleRefund(txn.id, mode, amount, reason)}
                          />
                        );
                      })()
                    ) : (
                      txn.status !== 'refunded' && txn.hasPayment && txn.status !== 'active' && (
                        <TouchableOpacity
                          style={{ marginTop: spacing.sm }}
                          onPress={() => setRefundConfirmId(txn.id)}
                        >
                          <Text style={{ color: colors.danger, fontSize: fontSize.sm, fontWeight: fontWeight.semibold }}>
                            {(txn.locationId && profile ? getLocationPermissions(profile, txn.locationId).refund_mode : permissions.refund_mode) === 'direct' ? 'Refund' : 'Request Refund'}
                          </Text>
                        </TouchableOpacity>
                      )
                    )}
                  </View>
                </View>
              ))}
              {txnTotal > 10 && (
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: spacing.sm }}>
                  <Button
                    title="Previous"
                    onPress={() => fetchTransactions(txnPage - 1)}
                    disabled={txnPage <= 1 || txnLoading}
                    size="sm"
                    variant="outline"
                  />
                  <Text style={styles.locationRole}>Page {txnPage} of {Math.ceil(txnTotal / 10)}</Text>
                  <Button
                    title="Next"
                    onPress={() => fetchTransactions(txnPage + 1)}
                    disabled={txnPage >= Math.ceil(txnTotal / 10) || txnLoading}
                    size="sm"
                    variant="outline"
                  />
                </View>
              )}
            </>
          )}
        </Card>
      )}

      {/* Hardware Status */}
      {permissions.view_hardware && controllerStatuses.length > 0 && (
        <Card style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Hardware Status</Text>
          {controllerStatuses.map((ctrl, idx) => (
            <View key={idx} style={styles.locationItem}>
              <View style={[styles.locationIcon, { backgroundColor: ctrl.online ? colors.success + '20' : colors.danger + '20' }]}>
                <Ionicons name="hardware-chip" size={20} color={ctrl.online ? colors.success : colors.danger} />
              </View>
              <View style={styles.locationInfo}>
                <Text style={styles.locationName}>{ctrl.controllerName || ctrl.controllerId}</Text>
                <Text style={styles.locationRole}>{ctrl.bayName} — {ctrl.online ? 'Online' : 'Offline'}</Text>
              </View>
            </View>
          ))}
        </Card>
      )}

      {/* Round 2 (S06): Equipment buttons = the owner's manual Advanced actions at this location */}
      {permissions.equipment_buttons && equipButtons.length > 0 && (
        <Card style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Equipment</Text>
          {equipButtons.map(b => (
            <View key={b.actionId} style={styles.locationItem}>
              <View style={styles.locationInfo}>
                <Text style={styles.locationName}>{b.name}</Text>
                <Text style={styles.locationRole}>{b.equipmentName}</Text>
              </View>
              <Button title={runningButton === b.actionId ? 'Running…' : 'Run'} size="sm" disabled={!!runningButton} onPress={async () => {
                setRunningButton(b.actionId);
                try { await apiPost('/api/attendants/equipment-buttons/run', { equipmentKind: b.equipmentKind, equipmentId: b.equipmentId, actionId: b.actionId }); toast.show({ type: 'success', title: `${b.name} started` }); }
                catch (err: any) { toast.show({ type: 'error', title: 'Could not run', message: err.message }); }
                finally { setRunningButton(null); }
              }} />
            </View>
          ))}
        </Card>
      )}

      {/* Recent Shifts */}
      <Card style={styles.sectionCard}>
        <Text style={styles.sectionTitle}>Recent Shifts</Text>
        {renderBoxSearch(shiftsSearch, setShiftsSearch, shiftsSearchRef, () => fetchShifts(1))}
        {shifts.length === 0 ? (
          <Text style={styles.emptyText}>No shift history</Text>
        ) : (
          shifts.map(shift => (
            <View key={shift.id} style={styles.shiftItem}>
              <View style={styles.shiftIcon}>
                <Ionicons
                  name={shift.clockOut ? "checkmark-circle" : "time"}
                  size={24}
                  color={shift.clockOut ? colors.success : colors.warning}
                />
              </View>
              <View style={styles.shiftDetails}>
                <View style={styles.shiftRow}>
                  <Text style={styles.shiftLocation}>{shift.locationName}</Text>
                  {shift.duration && (
                    <View style={styles.durationBadge}>
                      <Text style={styles.durationText}>{formatDuration(shift.duration)}</Text>
                    </View>
                  )}
                </View>
                <Text style={styles.shiftDate}>{formatDateTime(shift.clockIn)}</Text>
                {shift.clockOut && (
                  <Text style={styles.shiftClockOut}>
                    Ended: {formatDateTime(shift.clockOut)}
                  </Text>
                )}
                {shift.notes && (
                  <Text style={styles.shiftNotes}>"{shift.notes}"</Text>
                )}
                {!shift.clockOut && (
                  <View style={styles.activeShiftBadge}>
                    <View style={styles.pulseDot} />
                    <Text style={styles.activeShiftBadgeText}>Active</Text>
                  </View>
                )}
              </View>
            </View>
          ))
        )}
        {shiftsTotal > 10 && (
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: spacing.sm }}>
            <Button
              title="Previous"
              onPress={() => fetchShifts(shiftsPage - 1)}
              disabled={shiftsPage <= 1 || shiftsLoading}
              size="sm"
              variant="outline"
            />
            <Text style={styles.locationRole}>Page {shiftsPage} of {Math.ceil(shiftsTotal / 10)}</Text>
            <Button
              title="Next"
              onPress={() => fetchShifts(shiftsPage + 1)}
              disabled={shiftsPage >= Math.ceil(shiftsTotal / 10) || shiftsLoading}
              size="sm"
              variant="outline"
            />
          </View>
        )}
      </Card>

      <View style={{ height: spacing.xxl }} />
      </>);
      })()}
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: c.background,
  },
  scrollContent: {
    padding: spacing.md,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: c.background,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: c.background,
    padding: spacing.xl,
  },
  errorTitle: {
    fontSize: fontSize.xl,
    fontWeight: fontWeight.bold,
    color: c.text,
    marginTop: spacing.md,
  },
  errorText: {
    fontSize: fontSize.md,
    color: c.textSecondary,
    textAlign: 'center',
    marginTop: spacing.sm,
    maxWidth: rs(320),
  },
  emergencyCard: {
    borderWidth: 2,
    borderColor: c.danger + '40',
    marginBottom: spacing.md,
  },
  emergencyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  emergencyTitle: {
    flex: 1,
    fontSize: fontSize.md,
    fontWeight: fontWeight.bold,
    color: c.danger,
  },
  emergencyBody: {
    marginTop: spacing.md,
  },
  activeShiftCard: {
    backgroundColor: c.success + '10',
    borderWidth: 2,
    borderColor: c.success + '40',
  },
  activeShiftHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  pulseDotLarge: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: c.success,
    marginRight: spacing.md,
  },
  activeShiftInfo: {
    flex: 1,
  },
  activeShiftTitle: {
    fontSize: fontSize.lg,
    fontWeight: fontWeight.bold,
    color: c.success,
  },
  activeShiftLocation: {
    fontSize: fontSize.md,
    color: c.text,
    marginTop: 2,
  },
  timerContainer: {
    alignItems: 'center',
    paddingVertical: spacing.lg,
    marginBottom: spacing.md,
  },
  timerLabel: {
    fontSize: fontSize.sm,
    color: c.textSecondary,
    marginBottom: spacing.xs,
  },
  timerValue: {
    fontSize: fontSize.hero,
    fontWeight: fontWeight.bold,
    color: c.success,
    letterSpacing: 1,
  },
  clockInTime: {
    fontSize: fontSize.sm,
    color: c.textSecondary,
    marginBottom: spacing.md,
    textAlign: 'center',
  },
  boxSearchRow: { flexDirection: 'row', alignItems: 'center', gap: 6, borderWidth: 1, borderColor: c.border, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6, marginBottom: 10, backgroundColor: c.surface },
  boxSearchInput: { flex: 1, fontSize: 14, color: c.text, padding: 0 },
  notesInput: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  activeWashCard: {
    backgroundColor: c.primary + '10',
    borderWidth: 2,
    borderColor: c.primary + '40',
  },
  washSubtitle: {
    fontSize: fontSize.sm,
    color: c.textSecondary,
    marginBottom: spacing.md,
  },
  tierSection: {
    marginBottom: spacing.md,
  },
  tierLabel: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.semibold,
    color: c.text,
    marginBottom: spacing.sm,
  },
  tierOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    borderWidth: 1,
    borderColor: c.border,
    borderRadius: borderRadius.md,
    marginBottom: spacing.sm,
    backgroundColor: c.surface,
  },
  tierOptionSelected: {
    borderColor: c.primary,
    backgroundColor: c.primaryBg,
  },
  tierRadio: {
    width: rs(22),
    height: rs(22),
    borderRadius: rs(11),
    borderWidth: 2,
    borderColor: c.border,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  tierRadioDot: {
    width: rs(12),
    height: rs(12),
    borderRadius: rs(6),
    backgroundColor: c.primary,
  },
  tierName: {
    flex: 1,
    fontSize: fontSize.md,
    fontWeight: fontWeight.medium,
    color: c.text,
  },
  tierNameSelected: {
    color: c.primary,
    fontWeight: fontWeight.semibold,
  },
  tierPrice: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    color: c.textSecondary,
  },
  tierPriceSelected: {
    color: c.primary,
  },
  washHistoryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: c.borderLight,
  },
  washHistoryIcon: {
    width: rs(36),
    height: rs(36),
    borderRadius: rs(18),
    backgroundColor: c.primaryBg,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  washHistoryTitle: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.semibold,
    color: c.text,
  },
  washHistoryDetail: {
    fontSize: fontSize.xs,
    color: c.textSecondary,
    marginTop: 1,
  },
  washHistoryBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: borderRadius.sm,
  },
  washHistoryBadgeText: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
  },
  clockButton: {
    marginTop: spacing.md,
  },
  clockInCard: {
    alignItems: 'center',
  },
  clockInHeader: {
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  clockInTitle: {
    fontSize: fontSize.xxl,
    fontWeight: fontWeight.bold,
    color: c.text,
    marginTop: spacing.md,
  },
  clockInSubtitle: {
    fontSize: fontSize.md,
    color: c.textSecondary,
    marginTop: spacing.xs,
  },
  noLocations: {
    paddingVertical: spacing.xl,
  },
  noLocationsText: {
    fontSize: fontSize.md,
    color: c.textLight,
    textAlign: 'center',
  },
  sectionCard: {
    marginTop: spacing.md,
  },
  sectionTitle: {
    fontSize: fontSize.lg,
    fontWeight: fontWeight.bold,
    color: c.text,
    marginBottom: spacing.md,
  },
  emptyText: {
    fontSize: fontSize.sm,
    color: c.textLight,
    textAlign: 'center',
    paddingVertical: spacing.lg,
  },
  locationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: c.surface,
    borderRadius: borderRadius.md,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: c.border,
  },
  locationIcon: {
    width: rs(36),
    height: rs(36),
    borderRadius: rs(18),
    backgroundColor: c.primaryBg,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  locationInfo: {
    flex: 1,
  },
  locationName: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    color: c.text,
  },
  locationRole: {
    fontSize: fontSize.sm,
    color: c.textSecondary,
    marginTop: 2,
  },
  shiftItem: {
    flexDirection: 'row',
    padding: spacing.md,
    backgroundColor: c.surface,
    borderRadius: borderRadius.md,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: c.border,
  },
  shiftIcon: {
    marginRight: spacing.md,
  },
  shiftDetails: {
    flex: 1,
  },
  shiftRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  shiftLocation: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    color: c.text,
  },
  durationBadge: {
    backgroundColor: c.primaryBg,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: borderRadius.sm,
  },
  durationText: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
    color: c.primary,
  },
  shiftDate: {
    fontSize: fontSize.sm,
    color: c.textSecondary,
    marginTop: 4,
  },
  shiftClockOut: {
    fontSize: fontSize.sm,
    color: c.textLight,
    marginTop: 2,
  },
  shiftNotes: {
    fontSize: fontSize.sm,
    color: c.textSecondary,
    fontStyle: 'italic',
    marginTop: spacing.xs,
  },
  activeShiftBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.xs,
  },
  pulseDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: c.warning,
    marginRight: spacing.xs,
  },
  activeShiftBadgeText: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
    color: c.warning,
  },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
